import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SideNavigator } from '../components/SideNavigator';
import Heading from '../../../generic-components/Heading';
import Button from '../../../generic-components/Button';
import { LiliPasswordInput } from '../../../DesignSystem/LiliPasswordInput/LiliPasswordInput';
import { useSetPassword } from '../hooks/useSetPassword';
import { useStore } from '../../../store';
import { VIEWS_MAP } from '../../../router/constants';
import { OnboardingSteps } from '../consts';
import { useConsent } from '../hooks/useConsent';
import { useCompleteOnboarding } from '../hooks/useCompleteOnboarding';
import { trackMixPanel } from '../../../services/mixpanel.service';
import { extractErrorFromCode } from '../../../helpers';
import { IServerErrorResponse } from '../../../types';
import { FORGOT_PASSWORD_FLOW } from '../../../flows/login/pages/LoginHelp/constants';

export const SetPassword = () => {
  const [password, setPassword] = useState<string>('');
  const mfaData = useStore((state) => state.mfaData);
  const obData = useStore((state) => state.obData);
  const setObData = useStore((state) => state.setObData);
  const { mutateAsync: asyncSetPassword, isLoading, error } = useSetPassword();
  const { mutateAsync: asyncUploadConsent, isLoading: isLoadingConsent } = useConsent();
  const { mutateAsync: asyncCompleteOnboarding } = useCompleteOnboarding();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const handleSubmit = async () => {
    if (mfaData?.customerId && mfaData.authorizedExternalId && obData.accessToken) {
      const res = await asyncSetPassword({
        customerId: mfaData?.customerId,
        authorizedUserId: mfaData?.authorizedExternalId,
        password,
        token: obData.accessToken,
      });

      await asyncUploadConsent({
        customerId: mfaData?.customerId,
        authorizedUserId: mfaData?.authorizedExternalId,
        accessToken: res.token,
        formDataInstance: obData.consentFormData || new FormData(),
      });

      await asyncCompleteOnboarding().then(() => {
        navigate(`/${VIEWS_MAP.ACCOUNTANT_ONBOARDING.START}/${VIEWS_MAP.ACCOUNTANT_ONBOARDING.COMPLETE_OB}`, {
          replace: true,
        });
      });
    }
  };

  const errMsg = useMemo(() => {
    if (!error) return '';
    return extractErrorFromCode(error as IServerErrorResponse);
  }, [error]);

  useEffect(() => {
    trackMixPanel('Page view', { pageName: 'Accountant set password' });
    setObData({ ...obData, lastStepVisit: OnboardingSteps.createPassword });
  }, []);

  return (
    <div className='flex items-center grow h-full'>
      <SideNavigator />
      <div className='flex flex-col justify-center grow h-full bg-bw00 pl-[120px]'>
        <div className='flex flex-col justify-center grow max-w-[485px]'>
          <Heading
            title={t('ACCOUNTANT_OB.CREATE_PASSWORD.HEADING')}
            className='font-metricRMedium text-bw08 text-[28px] mb-[32px]'
          />
          <LiliPasswordInput
            id='password-input'
            title={t(FORGOT_PASSWORD_FLOW.NEW_PASSWORD.INPUT_TITLE)}
            styles='w-[360px] mb-[52px]'
            onChange={(data: string) => setPassword(data)}
            passwordStrengthTexts={{
              weak: t(FORGOT_PASSWORD_FLOW.NEW_PASSWORD.PASSWORD_STRENGTH_TOO_WEAK),
              tooWeak: t(FORGOT_PASSWORD_FLOW.NEW_PASSWORD.PASSWORD_STRENGTH_WEAK),
              medium: t(FORGOT_PASSWORD_FLOW.NEW_PASSWORD.PASSWORD_STRENGTH_FAIR),
              strong: t(FORGOT_PASSWORD_FLOW.NEW_PASSWORD.PASSWORD_STRENGTH_STRONG),
            }}
          />
          <Heading title={errMsg || ''} className='font-metricR text-red02 text-sm text-left mb-6' />
          <Button
            disable={!password}
            isLoading={isLoading || isLoadingConsent}
            onClick={handleSubmit}
            className='w-[230px] bg-action03 hover:bg-action04 font-metricRMedium text-bw00 py-[17px] rounded-[4px]'
            title={t('ACCOUNTANT_OB.CREATE_PASSWORD.CTA')}
            id='setPassword'
          />
        </div>
      </div>
    </div>
  );
};
